'use client';

/**
 * useUiPreferences — reactive access to the user's persona / feature
 * gating / nav-tab config.
 *
 * Reads the localStorage cache synchronously on first render (so the
 * bottom nav doesn't flash the wrong tabs), then hydrates from
 * Firestore. Subscribes to the `opmode-ui-prefs-changed` window event
 * so every consumer (nav, More hub, dashboard, settings) updates the
 * moment prefs are saved anywhere.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/lib/AuthContext';
import {
  type UiPreferences,
  readCachedPreferences,
  loadUiPreferences,
  saveUiPreferences,
  DEFAULT_UI_PREFERENCES,
} from '@/lib/uiPreferences';

export function useUiPreferences() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<UiPreferences | null>(() => readCachedPreferences());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user?.uid) { setLoaded(true); return; }
    (async () => {
      const loadedPrefs = await loadUiPreferences(user.uid);
      if (!cancelled) { setPrefs(loadedPrefs); setLoaded(true); }
    })();

    // Cross-component sync — any save fires this; re-read from cache
    // (already written by saveUiPreferences before the event).
    const onChanged = () => {
      const fresh = readCachedPreferences();
      if (fresh && !cancelled) setPrefs(fresh);
    };
    window.addEventListener('opmode-ui-prefs-changed', onChanged);
    return () => { cancelled = true; window.removeEventListener('opmode-ui-prefs-changed', onChanged); };
  }, [user?.uid]);

  const save = useCallback(async (next: UiPreferences) => {
    setPrefs(next); // optimistic
    if (user?.uid) await saveUiPreferences(user.uid, next);
  }, [user?.uid]);

  return {
    prefs: prefs ?? DEFAULT_UI_PREFERENCES,
    /** True once Firestore has been read (cache may have rendered earlier). */
    loaded,
    save,
  };
}
